import {
  type BackButtonListener,
  App as CapacitorApp,
  type RestoredListener,
  type StateChangeListener,
  type URLOpenListener,
} from "@capacitor/app";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import { Capacitor } from "@/lib/capacitor";
import { logger } from "@/logger";

export const App = (function () {
  const appLogger = logger.getLogger("@capacitor/app");

  async function exitApp() {
    return await CapacitorApp.exitApp();
  }

  async function getInfo() {
    if (Capacitor.Platform === "web") {
      appLogger.trace("App.getInfo() is not available on Web");
      return;
    }

    return await CapacitorApp.getInfo();
  }

  async function getState() {
    return (await CapacitorApp.getState()).isActive;
  }

  async function getLaunchUrl() {
    return await CapacitorApp.getLaunchUrl();
  }

  async function minimizeApp() {
    return await CapacitorApp.minimizeApp();
  }

  /**
   * De instellingen van deze app op het toestel.
   *
   * Dit is de enige uitweg zodra iemand een toestemming één keer geweigerd heeft: het systeem
   * vraagt het daarna niet meer, dus `requestPermissions()` komt meteen terug met "denied" en de
   * app kan er zelf niets meer aan doen. Wie dan alleen te horen krijgt dát het niet mag, staat
   * stil -- vandaar dat elke geweigerde toestemming een knop hiernaartoe hoort te hebben.
   *
   * Het ging hier eerder mis: op iOS stond `window.open("app-settings:", "_system")`, en dat doet
   * in een WKWebView niets (`_system` is een Cordova-begrip, geen browser-begrip), en op Android
   * stond er alleen een waarschuwing in het log. `capacitor-native-settings` zat al in beide native
   * projecten -- er werd alleen nooit iets aan gevraagd.
   */
  async function openSettings() {
    if (Capacitor.Platform === "web") {
      appLogger.trace("App.openSettings() is not available on Web");
      return;
    }

    try {
      if (Capacitor.Platform === "ios") {
        // App is de enige optie die Apple ondersteunt; de rest kan een afkeuring in de store
        // opleveren.
        await NativeSettings.openIOS({ option: IOSSettings.App });
        return;
      }

      await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
    } catch (error) {
      // Niet kunnen openen is vervelend, maar geen reden om het scherm eronder om te trekken.
      appLogger.error("App.openSettings() failed", { error });
    }
  }

  function addStateChangeListener(listenerFunc: StateChangeListener) {
    return CapacitorApp.addListener("appStateChange", listenerFunc);
  }

  function addUrlOpenListener(listenerFunc: URLOpenListener) {
    return CapacitorApp.addListener("appUrlOpen", listenerFunc);
  }

  function addRestoredResultListener(listenerFunc: RestoredListener) {
    return CapacitorApp.addListener("appRestoredResult", listenerFunc);
  }

  function addBackButtonListener(listenerFunc: BackButtonListener) {
    return CapacitorApp.addListener("backButton", listenerFunc);
  }

  function removeAllListeners() {
    return CapacitorApp.removeAllListeners();
  }

  return {
    ...CapacitorApp,
    exitApp,
    getInfo,
    getState,
    getLaunchUrl,
    minimizeApp,
    openSettings,
    addStateChangeListener,
    addUrlOpenListener,
    addRestoredResultListener,
    addBackButtonListener,
    removeAllListeners,
  };
})();

export type * from "@capacitor/app";
